import { updateSpeakerNameInCache } from '@src/stores-v2/speakerCache';
import { LogManager } from '@src/utility/Logger';
import React, { useCallback, useRef, useState } from 'react';

import SpeakerFormModal from '../SpeakerFormModal';

import type { DatoSpeaker } from '@src/repositories/SpeakerRepository';
import type {
  SpeakerData,
  SpeakerManagementService,
} from '@src/services/SpeakerManagementService';

const logger = LogManager.getLogger('transcript:useEditSpeaker');

/**
 * "Edit Speaker" from the transcript's speaker-label menu: resolves the clicked
 * label to its Dato speaker, opens the speaker form, and writes the new name
 * through to the label cache so the transcript updates without a CDN re-fetch.
 * Extracted from `Transcript.tsx` for its line budget.
 */
export function useEditSpeaker(
  speakerService: SpeakerManagementService,
  storyId: string | undefined,
) {
  const [toEdit, setToEdit] = useState<{
    speaker: DatoSpeaker;
    speakerLabel: string;
  } | null>(null);

  // Only the latest click may open the form; an earlier, slower lookup is dropped.
  const latestRequestRef = useRef(0);

  const openEditSpeaker = useCallback(
    async (speakerLabel: string) => {
      if (!storyId) return;
      const requestId = ++latestRequestRef.current;
      const result = await speakerService.findSpeakerForLabel(
        storyId,
        speakerLabel,
      );
      if (requestId !== latestRequestRef.current) return;
      if (!result.ok) {
        logger.error('Failed to look up speaker to edit', {
          speakerLabel,
          error: result.error,
        });
      } else if (!result.value) {
        logger.error('No Dato speaker mapped to label', { speakerLabel });
      } else {
        setToEdit({ speaker: result.value, speakerLabel });
      }
    },
    [speakerService, storyId],
  );

  const handleUpdateSpeaker = useCallback(
    async (speakerData: SpeakerData): Promise<boolean> => {
      if (!toEdit) return false;
      const result = await speakerService.updateSpeaker(
        toEdit.speaker.id,
        speakerData,
      );
      if (!result.ok) {
        logger.error('Failed to update speaker', result.error);
        return false;
      }
      if (storyId) {
        updateSpeakerNameInCache(
          storyId,
          toEdit.speakerLabel,
          speakerData.name,
          toEdit.speaker.id,
        );
      }
      setToEdit(null);
      return true;
    },
    [toEdit, speakerService, storyId],
  );

  const editSpeakerModal = toEdit && (
    <SpeakerFormModal
      isOpen
      onClose={() => setToEdit(null)}
      onSubmit={handleUpdateSpeaker}
      speakerToEdit={{
        speakerLabel: '',
        datoSpeakerId: toEdit.speaker.id,
        name: toEdit.speaker.name,
        title: toEdit.speaker.title,
        bio: toEdit.speaker.bio,
        voice: toEdit.speaker.voice,
        gender: toEdit.speaker.gender,
        headshot: toEdit.speaker.headshots?.[0]?.url || null,
        headshots: toEdit.speaker.headshots || [],
      }}
    />
  );

  return { openEditSpeaker, editSpeakerModal };
}
